import { del, get, patch, post, put, type RequestOptions } from '@/api/client';
import type {
  CalInsightsViewApi,
  CellColorThreshold,
  EmailIntegration,
  EnabledFlags,
  GeneralInfo,
  KpiVisibility,
  OppWindow,
  PlaybookRulesResponse,
  ProductionGoals,
  ProviderNetwork,
  SettingsAppointmentReason,
  SettingsCarrier,
  SettingsOperatory,
  SettingsProvider,
} from './types';

export const settingsApi = {
  getGeneralInfo: (locationId: number, options?: RequestOptions) =>
    get<GeneralInfo>('/api/settings/general-info', { locationId }, options),
  updateGeneralInfo: (locationId: number, data: Partial<GeneralInfo>) =>
    put<GeneralInfo>('/api/settings/general-info', data, { locationId }),

  getOperatories: (locationId: number, includeInactive = false, options?: RequestOptions) =>
    get<{ operatories: SettingsOperatory[] }>(
      '/api/settings/operatories/',
      {
        locationId,
        ...(includeInactive ? { include_inactive: true } : {}),
      },
      options,
    ),

  createOperatory: (locationId: number, data: Partial<SettingsOperatory>) =>
    post<SettingsOperatory>('/api/settings/operatories/', data, { locationId }),

  updateOperatory: (locationId: number, id: string, data: Partial<SettingsOperatory>) =>
    put<SettingsOperatory>(`/api/settings/operatories/${id}/`, data, { locationId }),

  deleteOperatory: (locationId: number, id: string) =>
    del<void>(`/api/settings/operatories/${id}/`, { locationId }),

  getProviders: (includeInactive = false, options?: RequestOptions) =>
    get<{ providers: SettingsProvider[] }>(
      '/api/settings/providers/',
      {
        ...(includeInactive ? { include_inactive: true } : {}),
      },
      options,
    ),

  updateProvider: (id: string, data: Partial<SettingsProvider>) =>
    put<SettingsProvider>(`/api/settings/providers/${id}/`, data),

  updateProviderNetwork: (providerId: string, data: Partial<ProviderNetwork>) =>
    put<ProviderNetwork>(`/api/settings/provider-network/${providerId}`, data),

  getCarriers: (includeInactive = false, options?: RequestOptions) =>
    get<{ results: SettingsCarrier[] }>(
      '/api/settings/accepted-carriers/',
      {
        ...(includeInactive ? { include_inactive: true } : {}),
      },
      options,
    ),

  createCarrier: (data: Partial<SettingsCarrier>) =>
    post<SettingsCarrier>('/api/settings/accepted-carriers/', data),

  deleteCarrier: (carrierId: number) => del<void>(`/api/settings/accepted-carriers/${carrierId}/`),

  getAppointmentReasons: (locationId: number, includeInactive = false, options?: RequestOptions) =>
    get<{ appointmentReasons: SettingsAppointmentReason[] }>(
      '/api/settings/appointment-reasons/',
      {
        locationId,
        ...(includeInactive ? { include_inactive: true } : {}),
      },
      options,
    ),

  createAppointmentReason: (locationId: number, data: Partial<SettingsAppointmentReason>) =>
    post<SettingsAppointmentReason>('/api/settings/appointment-reasons/', data, { locationId }),

  updateAppointmentReason: (
    locationId: number,
    id: string,
    data: Partial<SettingsAppointmentReason>,
  ) => patch<SettingsAppointmentReason>(`/api/settings/appointment-reasons/${id}/`, data, { locationId }),

  deleteAppointmentReason: (locationId: number, id: string) =>
    del<void>(`/api/settings/appointment-reasons/${id}/`, { locationId }),

  getPlaybookRules: (scope?: string, options?: RequestOptions) =>
    get<PlaybookRulesResponse>('/api/settings/playbook/rules', scope ? { scope } : {}, options),

  createPlaybookRule: (data: { text: string; category: string }) =>
    post<{ rule_id: string }>('/api/settings/playbook/rules', data),

  updatePlaybookRule: (ruleId: string, data: { text?: string; active?: boolean }) =>
    patch<void>(`/api/settings/playbook/rules/${ruleId}`, data),

  deletePlaybookRule: (ruleId: string) => del<void>(`/api/settings/playbook/rules/${ruleId}`),

  analyzePlaybookRule: (text: string) =>
    post<{ clarity: string; suggestions: string[] }>('/api/settings/playbook/rules/analyze', {
      text,
    }),

  getCalInsightsViews: (options?: RequestOptions) =>
    get<{ views: Record<string, CalInsightsViewApi> }>(
      '/api/settings/calendar-insights/views',
      undefined,
      options,
    ),

  getCalInsightsView: (view: string, options?: RequestOptions) =>
    get<unknown>(`/api/settings/calendar-insights/views/${view}`, undefined, options),

  updateCalInsightsView: (view: string, data: unknown) =>
    put<unknown>(`/api/settings/calendar-insights/views/${view}`, data),

  getKpiVisibility: (view?: string, options?: RequestOptions) =>
    view
      ? get<KpiVisibility>(`/api/settings/kpi/visibility/${view}`, undefined, options)
      : get<KpiVisibility>('/api/settings/kpi/visibility', undefined, options),

  updateKpiVisibility: (view: string, data: Partial<KpiVisibility>) =>
    put<KpiVisibility>(`/api/settings/kpi/visibility/${view}`, data),

  getOppWindows: (options?: RequestOptions) =>
    get<{ windows: OppWindow[] }>('/api/settings/opportunity-windows/', undefined, options),

  createOppWindow: (data: Partial<OppWindow>) =>
    post<OppWindow>('/api/settings/opportunity-windows/', data),

  updateOppWindow: (id: string, data: Partial<OppWindow>) =>
    patch<OppWindow>(`/api/settings/opportunity-windows/${id}/`, data),

  deleteOppWindow: (id: string) => del<void>(`/api/settings/opportunity-windows/${id}/`),

  getCellColorThresholds: (options?: RequestOptions) =>
    get<{ thresholds: CellColorThreshold[] }>(
      '/api/settings/cell-color-thresholds/',
      undefined,
      options,
    ),

  updateCellColorThreshold: (id: string, data: Partial<CellColorThreshold>) =>
    patch<CellColorThreshold>(`/api/settings/cell-color-thresholds/${id}/`, data),

  getEnabledFlags: (options?: RequestOptions) =>
    get<EnabledFlags>('/api/settings/enabled-flags/', undefined, options),

  updateEnabledFlags: (data: { enabled: string[] }) =>
    put<EnabledFlags>('/api/settings/enabled-flags/', data),

  getProductionGoals: (options?: RequestOptions) =>
    get<ProductionGoals>('/api/settings/production-goals/', undefined, options),

  updateProductionGoals: (data: Partial<ProductionGoals>) =>
    put<ProductionGoals>('/api/settings/production-goals/', data),

  getEmailIntegration: (locationId?: number, options?: RequestOptions) =>
    get<EmailIntegration>(
      '/api/settings/email-integration',
      locationId != null ? { locationId } : undefined,
      options,
    ),

  updateEmailIntegration: (data: Partial<EmailIntegration>, locationId?: number) =>
    put<EmailIntegration>(
      '/api/settings/email-integration',
      data,
      locationId != null ? { locationId } : undefined,
    ),

  startEmailIntegrationOauth: (provider: 'gmail' | 'outlook') =>
    post<unknown>('/api/settings/email-integration/oauth/start', { provider }),

  completeEmailIntegrationOauth: (payload: {
    code: string;
    state: string;
    provider: string;
  }) => post<void>('/api/settings/email-integration/oauth/callback', payload),
};
